import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { enviarJson } from "../../nucleo/api";
import { mensagemDoConsole } from "../../nucleo/console";
import { formatarData } from "../../nucleo/implantacoes";
import type { RevisaoDoKickoff } from "../../nucleo/kickoff";
import {
  caminhoDosMarcos,
  chaveDosMarcos,
  esquemaDosMarcos,
  type MarcosDoKickoff,
  useMarcosDoKickoff,
} from "../../nucleo/marcosDoKickoff";
import { Aviso, Botao, Cartao } from "../ui";

export function AtosDoKickoff({ revisao, podeGerar }: { revisao: RevisaoDoKickoff; podeGerar: boolean }) {
  const cliente = useQueryClient();
  const base = caminhoDosMarcos(revisao.implantacao_id);
  const marcos = useMarcosDoKickoff(revisao.implantacao_id);
  const ato = useMutation({
    mutationFn: (operacao: "tarefa" | "conclusao") => enviarJson(`${base}/${operacao}`, esquemaDosMarcos, {}),
    onSuccess: (resposta) => {
      cliente.setQueryData(chaveDosMarcos(revisao.implantacao_id), resposta);
      void cliente.invalidateQueries({ queryKey: ["implantacoes"] });
      void cliente.invalidateQueries({ queryKey: ["console"] });
      void cliente.invalidateQueries({ queryKey: ["pacotes"] });
    },
  });
  const dados = marcos.data;
  useEffect(() => {
    if (dados?.tarefa_gerada_em) {
      void cliente.invalidateQueries({ queryKey: ["pacotes"] });
      void cliente.invalidateQueries({ queryKey: ["implantacoes"] });
    }
  }, [cliente, dados?.tarefa_gerada_em]);
  const completas = revisao.trilhas.every((t) => t.chave === "infraestrutura" || t.sessao.estado === "enviada");
  const aceito = revisao.aceite != null && revisao.aceite.trilhas_pendentes.length === 0;
  const erro = ato.isError || marcos.isError ? <Aviso tom="erro">{mensagemDoConsole(ato.error ?? marcos.error)}</Aviso> : null;
  if (dados?.modo === "geotask") {
    return (
      <AtosNoGeoTask dados={dados} podeGerar={podeGerar} completas={completas} aceito={aceito}
        ocupado={ato.isPending} erro={erro} agir={(operacao) => ato.mutate(operacao)} />
    );
  }
  return (
    <Cartao titulo="Tarefa e conclusão do kickoff" data-testid="atos-do-kickoff">
      {marcos.isPending ? <p role="status">Carregando marcos do kickoff…</p> : null}
      <p>Gerar a tarefa registra as respostas no Jira. O prazo começa quando a tarefa for confirmada e a Infra estiver liberada.</p>
      <p className="mt-2">Concluir o kickoff libera a leitura das tarefas. A execução aguarda a reunião de kickoff.</p>
      {dados?.tarefa_gerada_em ? <p data-testid="tarefa-kickoff-gerada">Tarefa gerada no Jira em {dados.tarefa_gerada_em}.</p> : null}
      {dados?.concluido_em ? <p data-testid="kickoff-concluido">Kickoff concluído no portal.</p> : null}
      {dados?.jira_pendencia ? <Aviso tom="atencao">{dados.jira_pendencia}</Aviso> : null}
      {erro}
      <div className="acoes-do-console mt-3">
        <Botao data-testid="gerar-tarefa-kickoff" disabled={!podeGerar || !completas || !dados || !!dados.tarefa_gerada_em || ato.isPending} onClick={() => ato.mutate("tarefa")}>Gerar tarefa de kickoff</Botao>
        <Botao data-testid="concluir-kickoff" disabled={!aceito || !dados?.tarefa_gerada_em || !!dados.concluido_em || ato.isPending} onClick={() => ato.mutate("conclusao")}>Concluir kickoff</Botao>
      </div>
    </Cartao>
  );
}

/** A primeira condição que falta para gerar a K-O; `null` quando o botão pode ser usado. */
function faltaParaGerar(dados: MarcosDoKickoff, podeGerar: boolean, completas: boolean, aceito: boolean): string | null {
  if (!podeGerar) return "Seu papel não gera a tarefa de K-O.";
  if (!completas) return "Falta o cliente enviar todas as trilhas dos módulos.";
  if (!aceito) return "Aceite as respostas antes de gerar a K-O.";
  if (dados.geotask_key && !dados.geotask_pendencia) return "A K-O já foi gerada e está sincronizada.";
  return null;
}

function AtosNoGeoTask({ dados, podeGerar, completas, aceito, ocupado, erro, agir }: {
  dados: MarcosDoKickoff;
  podeGerar: boolean;
  completas: boolean;
  aceito: boolean;
  ocupado: boolean;
  erro: ReactNode;
  agir: (operacao: "tarefa" | "conclusao") => void;
}) {
  const falta = faltaParaGerar(dados, podeGerar, completas, aceito);
  return (
    <Cartao titulo="Tarefa e conclusão do kickoff" data-testid="atos-do-kickoff">
      <p>Passo 2: depois do aceite, gere a K-O no GeoTask. O prazo começa quando a tarefa for confirmada e a Infra estiver liberada.</p>
      <p className="mt-2">Concluir o kickoff libera a leitura das tarefas. A execução aguarda a reunião de kickoff.</p>
      <div role="status" aria-live="polite" data-testid="situacao-da-ko">
        {dados.geotask_key ? <p data-testid="tarefa-kickoff-gerada">Tarefa {dados.geotask_key} gerada no GeoTask em {formatarData(dados.tarefa_gerada_em)}.</p> : null}
        {dados.concluido_em ? <p data-testid="kickoff-concluido">Kickoff concluído no portal.</p> : null}
        {dados.geotask_pendencia ? <Aviso tom="atencao">{dados.geotask_pendencia}</Aviso> : null}
        {dados.aviso_segmento ? <Aviso tom="atencao">{dados.aviso_segmento}</Aviso> : null}
      </div>
      {erro}
      {falta && !dados.geotask_key ? <p className="nota-rodape mt-2" id="motivo-gerar-ko" data-testid="motivo-gerar-ko">{falta}</p> : null}
      <div className="acoes-do-console mt-3">
        <Botao data-testid="gerar-tarefa-kickoff" disabled={!!falta || ocupado}
          aria-describedby={falta && !dados.geotask_key ? "motivo-gerar-ko" : undefined}
          onClick={() => agir("tarefa")}>
          {ocupado ? "Sincronizando…" : dados.geotask_key ? "Sincronizar pendências" : "Gerar tarefa de K-O"}
        </Botao>
        <Botao data-testid="concluir-kickoff" disabled={!aceito || !dados.geotask_key || !!dados.geotask_pendencia || !!dados.concluido_em || ocupado} onClick={() => agir("conclusao")}>Concluir kickoff</Botao>
      </div>
    </Cartao>
  );
}
